import { LocateFixed, X } from 'lucide-react';
import { Suspense, lazy, useCallback, useState } from 'react';

import { cn } from '@/shared/lib/utils';
import { Button } from '@/shared/ui/shadcn/button';

import { AbrirEnMapasButton } from './abrir-en-mapas-button';
import { CoordenadasInputs } from './coordenadas-inputs';

import type { LatLng } from './location-picker-map';

export type { LatLng } from './location-picker-map';

// El mapa (Leaflet) se carga en diferido: su código y CSS quedan fuera del
// bundle de primer paint y solo se descargan al abrir un formulario que lo
// use. El fallback de Suspense muestra un placeholder mientras tanto.
const LocationPickerMap = lazy(() => import('./location-picker-map'));

/**
 * Centro inicial cuando no hay valor: Guatemala (mismo centro que el visor
 * geográfico del proyecto). El caller puede sobrescribirlo si conoce la zona.
 */
const DEFAULT_CENTER: LatLng = { lat: 15.7835, lng: -90.2308 };

export interface LocationPickerProps {
  value: LatLng | null;
  onChange: (value: LatLng | null) => void;
  defaultCenter?: LatLng;
  disabled?: boolean;
  className?: string;
}

/**
 * Selector de ubicación sobre un mapa: clic o arrastre del pin fija las
 * coordenadas; el botón "Usar mi ubicación actual" las toma del navegador y
 * los campos de latitud/longitud permiten escribirlas o pegarlas (p. ej. desde
 * Google Maps o un GPS de mano). Las tres vías quedan sincronizadas.
 * Componente controlado (`value` / `onChange`); `value` es `null` mientras
 * no haya ubicación. Pensado para campos opcionales de geolocalización.
 */
export function LocationPicker({
  value,
  onChange,
  defaultCenter = DEFAULT_CENTER,
  disabled = false,
  className,
}: LocationPickerProps) {
  const [recenterTo, setRecenterTo] = useState<LatLng | null>(null);
  const [geoError, setGeoError] = useState<string | null>(null);
  const [locating, setLocating] = useState(false);

  const usarMiUbicacion = useCallback(() => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      setGeoError('La geolocalización no está disponible en este dispositivo.');
      return;
    }
    setGeoError(null);
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const ll = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        onChange(ll);
        setRecenterTo(ll);
        setLocating(false);
      },
      () => {
        setGeoError('No se pudo obtener su ubicación. Revise los permisos del navegador.');
        setLocating(false);
      },
      { enableHighAccuracy: true, timeout: 10_000 },
    );
  }, [onChange]);

  return (
    <div className={cn('space-y-2', className)}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={usarMiUbicacion}
          disabled={disabled || locating}
        >
          <LocateFixed aria-hidden="true" />
          {locating ? 'Ubicando…' : 'Usar mi ubicación actual'}
        </Button>
        {value ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => onChange(null)}
            disabled={disabled}
            aria-label="Quitar ubicación"
            className="text-muted-foreground hover:text-destructive"
          >
            <X aria-hidden="true" />
            Quitar
          </Button>
        ) : null}
      </div>

      <CoordenadasInputs
        value={value}
        onChange={onChange}
        onConfirmar={setRecenterTo}
        disabled={disabled}
      />

      <div
        className={cn(
          'h-64 w-full overflow-hidden rounded-xl border border-border',
          disabled && 'pointer-events-none opacity-60',
        )}
      >
        <Suspense
          fallback={
            <div className="flex h-full items-center justify-center bg-muted/30 text-xs text-muted-foreground">
              Cargando mapa…
            </div>
          }
        >
          <LocationPickerMap
            value={value}
            center={value ?? defaultCenter}
            recenterTo={recenterTo}
            onPick={onChange}
          />
        </Suspense>
      </div>

      {geoError ? (
        <p className="text-xs text-destructive" aria-live="polite">
          {geoError}
        </p>
      ) : null}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-[11px] text-muted-foreground">
          Escriba o pegue las coordenadas, haga clic en el mapa o arrastre el marcador.
        </p>
        {value ? <AbrirEnMapasButton value={value} /> : null}
      </div>
    </div>
  );
}
